import React, { FunctionComponent, PropsWithChildren, useState } from "react";

import { bemClassNames, createQA } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { QAProps } from "@multiweb/types/react";
import { Icon } from "../Icon";
import { FlexDirection, JustifyContent, Layout, SpacingSize } from "../Layout";
import { FontWeight, Text } from "../Text";
import { AlignItems } from "../types";

export type CollapsibleProps = PropsWithChildren<{
  /** příznak umožňující externí minimalizaci/expanzi collapsible komponenty */
  handleExpansionExternally?: boolean;
  /** zobrazí název pro rozevírací seznam */
  captionText: string;
  /** číselná hodnota (px), nastavující výšku viditelnosti seznamu při sbalení – 0 značí úplně sbaleno */
  collapsedHeight?: number;
  /** defaultní hodnota, nastavující, zda je seznam otevřen */
  defaultOpened?: boolean;
  /** callback volaný při sbalení/rozbalení seznamu */
  onExpansionChange?: (isExpanded: boolean) => void;
} & QAProps>;

const classNames = bemClassNames("collapsible-component");

const collapsibleBtnDataQA = createQA("collapsible-component-btn");
const collapsibleContentDataQA = createQA("collapsible-component-content");

export const Collapsible: FunctionComponent<CollapsibleProps> = ({
    handleExpansionExternally = false,
    captionText,
    children,
    collapsedHeight = 0,
    defaultOpened = false,
    onExpansionChange,
    qa,
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultOpened);

    const isReallyExpanded = handleExpansionExternally ? defaultOpened : isExpanded;

    const handleClick = () => {
        if (!handleExpansionExternally)
            setIsExpanded((prevIsExpanded) => !prevIsExpanded);

        if (onExpansionChange)
            onExpansionChange(!isReallyExpanded);
    };

    return (
        <Layout qa={qa} flexDirection={FlexDirection.column}>
            <button {...collapsibleBtnDataQA} className={classNames({ "is-open": isReallyExpanded })} onClick={handleClick}>
                <Layout alignItems={AlignItems.center} justifyContent={JustifyContent.spaceBetween}>
                    <Layout spacingSize={SpacingSize.s}>
                        <Text fontWeight={FontWeight.bold} value={captionText} />
                    </Layout>
                    <span className={classNames("icon", { rotate: isReallyExpanded })}>
                        <Layout spacingSize={SpacingSize.s}>
                            <Icon iconCode={IconCode.uiChevronBottom} />
                        </Layout>
                    </span>
                </Layout>
            </button>
            <div {...collapsibleContentDataQA} className={classNames("content", { "expanded": isReallyExpanded })}>
                <div style={{ minHeight: collapsedHeight }}>
                    {children}
                </div>
            </div>
        </Layout>
    );
};
